import { createClient as createWsClient } from 'graphql-ws'
import { authExchange } from '@urql/exchange-auth'
import { createClient, fetchExchange, subscriptionExchange, Client, CombinedError, mapExchange } from 'urql'
import { getAccessToken, getTokens, setTokens, Tokens } from '@/auth/tokenStore'
import { postLogout, postTokensUpdated } from '@/features/auth/utils/authBroadcast'
import { parseReason, RefreshSuccessSchema } from '@/features/auth/schemas/tokens'
import { buildLoginRedirect } from 'src/features/auth/utils/loginRedirect'

function getApiUrl(): string {
  return '/v1/graphql'
}

const WS_URL = typeof window !== 'undefined'
  ? `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/v1/graphql`
  : undefined

let wsClient: ReturnType<typeof createWsClient> | null = null

function createWsClientInstance(): ReturnType<typeof createWsClient> | null {
  if (!WS_URL) return null

  return createWsClient({
    url: WS_URL,
    lazy: true,
    keepAlive: 12_000,
    connectionParams: () => {
      const t = getAccessToken()
      return t
        ? { headers: { Authorization: `Bearer ${t}` } }
        : { headers: { 'x-hasura-role': 'public' } }
    },
  })
}

export function forceWsReconnect(): void {
  if (wsClient) {
    wsClient.dispose()
  }
  wsClient = createWsClientInstance()
}

// Mutex to prevent concurrent refresh attempts (reuse detection)
let refreshPromise: Promise<Tokens> | null = null

async function doServerRefresh(): Promise<Tokens> {
  // If a refresh is already in flight, wait for it instead of starting a new one
  if (refreshPromise) {
    return refreshPromise
  }

  refreshPromise = doRefreshRequest()
  try {
    return await refreshPromise
  } finally {
    refreshPromise = null
  }
}

export class RefreshRejectedError extends Error {
  readonly reason: ReturnType<typeof parseReason>
  constructor(reason: ReturnType<typeof parseReason>, message: string) {
    super(message)
    this.name = 'RefreshRejectedError'
    this.reason = reason
  }
}

async function doRefreshRequest(): Promise<Tokens> {
  const currentTokens = getTokens()
  if (!currentTokens?.refresh_token) {
    throw new RefreshRejectedError('expired', 'No refresh token available')
  }

  const response = await fetch(getApiUrl(), {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({
      query: 'mutation Refresh($refresh_token: String!) { Refresh(refresh_token: $refresh_token) { access_token access_token_expiry refresh_token refresh_token_expiry session_started_at } }',
      variables: { refresh_token: currentTokens.refresh_token },
    }),
    credentials: 'include',
  })

  const result = await response.json()

  if (result?.data?.Refresh) {
    const parsed = RefreshSuccessSchema.safeParse(result.data.Refresh)
    if (!parsed.success) {
      throw new RefreshRejectedError('expired', 'Refresh response shape invalid')
    }
    return {
      access_token: parsed.data.access_token,
      access_token_expiry: Date.parse(parsed.data.access_token_expiry),
      refresh_token: parsed.data.refresh_token,
      refresh_token_expiry: Date.parse(parsed.data.refresh_token_expiry),
      session_started_at: Date.parse(parsed.data.session_started_at),
      account_type: currentTokens.account_type,
      email: currentTokens.email,
      id: currentTokens.id,
    }
  }

  const graphqlError = Array.isArray(result?.errors) ? result.errors[0] : null
  const extensions = (graphqlError?.extensions ?? {}) as Record<string, unknown>
  const reason = parseReason(extensions.reason)
  throw new RefreshRejectedError(reason, graphqlError?.message ?? 'Refresh failed')
}

/**
 * Called by the proactive-refresh hook before an access token expires.
 * Single-flight via the existing refresh mutex. Redirects on rejection.
 */
export async function triggerProactiveRefresh(): Promise<void> {
  try {
    const next = await doServerRefresh()
    setTokens(next)
    postTokensUpdated(next)
    forceWsReconnect()
  } catch (err) {
    if (err instanceof RefreshRejectedError) {
      const reason = err.reason
      setTokens(null)
      forceWsReconnect()
      postLogout(reason)
      if (typeof window !== 'undefined') {
        window.location.href = buildLoginRedirect(
          reason,
          window.location.pathname + window.location.search,
        )
      }
      return
    }
    console.warn('[Auth] Proactive refresh transient failure (kept session):', err instanceof Error ? err.message : err)
  }
}

// Initialize WebSocket client
if (typeof window !== 'undefined') {
  wsClient = createWsClientInstance()
}

const customFetch = (
  url: string | URL | Request,
  options?: RequestInit
): Promise<Response> => {
  if (typeof url === 'string' && typeof options?.body === 'string') {
    try {
      const body = JSON.parse(options.body) as Record<string, unknown>
      const extensions = body.extensions as Record<string, unknown> | undefined
      if (extensions?.persistedQuery) {
        delete extensions.persistedQuery
        if (Object.keys(extensions).length === 0) {
          delete (body as Record<string, unknown>).extensions
        }
        options.body = JSON.stringify(body)
      }
    } catch {
      // If body isn't valid JSON, leave it as is
    }
  }
  // Never let the browser's HTTP cache serve a stale GraphQL response.
  // urql itself has no cacheExchange (see `exchanges` below) and runs
  // `network-only`, so the only place a stale read can come from is the
  // browser cache — e.g. a freshly-added "autre demandeur" card not showing
  // up until a hard page reset (#474). `no-store` guarantees every operation
  // is fetched fresh and never written to the cache, for any request method.
  const fetchOptions: RequestInit = { ...options, cache: 'no-store' }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return fetch(url as any, fetchOptions)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const urqlClient: Client = createClient({
  url: getApiUrl(),
  requestPolicy: 'network-only',
  fetch: customFetch,
  exchanges: [
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (authExchange(async (utils: any) => ({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      addAuthToOperation(operation: any) {
        const t = getAccessToken()
        if (!t) {
          return operation
        }
        return utils.appendHeaders(operation, {
          Authorization: `Bearer ${t}`,
        })
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      didAuthError(error: any) {
        const cError = error as CombinedError
        return cError.graphQLErrors?.some((e) =>
          e.message?.includes('JWTExpired') ||
          e.message?.includes('Could not verify JWT') ||
          (e.extensions as Record<string, unknown>)?.code === 'invalid-jwt'
        ) ?? false
      },
      async refreshAuth() {
        try {
          const next = await doServerRefresh()
          setTokens(next)
          postTokensUpdated(next)
          forceWsReconnect()
        } catch (err) {
          if (err instanceof RefreshRejectedError) {
            const reason = err.reason
            console.warn('[Auth] Token refresh rejected:', reason, err.message)
            setTokens(null)
            forceWsReconnect()
            postLogout(reason)
            if (typeof window !== 'undefined') {
              window.location.href = buildLoginRedirect(
                reason,
                window.location.pathname + window.location.search,
              )
            }
            return
          }
          // Transient errors (network, 500) — keep tokens, don't redirect, authExchange will retry on next op.
          console.warn('[Auth] Token refresh transient failure (kept session):', err instanceof Error ? err.message : err)
        }
      },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    })) as any),
    mapExchange({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onOperation(operation: any) {
        if (operation.extensions?.persistedQuery) {
          delete operation.extensions.persistedQuery
          if (Object.keys(operation.extensions).length === 0) {
            delete operation.extensions
          }
        }
        if (operation.context?.persistedQuery) {
          delete operation.context.persistedQuery
        }
        return operation
      },
    }),
    mapExchange({
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onOperation(operation: any) {
        operation.context.preferGetMethod = false
        return operation
      },
    }),
    fetchExchange,
    ...(wsClient
      ? [
          subscriptionExchange({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            forwardSubscription: (req: any) => ({
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              subscribe: (sink: any) => ({
                unsubscribe: wsClient!.subscribe(req, sink),
              }),
            }),
          }),
        ]
      : []),
  ],
})
